import type { ReactNode } from 'react';
import { ArrowLeft, X } from 'lucide-react';
import type { MotionProps } from 'motion/react';
import { motion } from 'motion/react';
import { cn } from '../ui/utils';

type FullscreenShellProps = {
  title: string;
  onClose: () => void;
  /** If omitted, back button calls onClose */
  onBack?: () => void;
  children: ReactNode;

  /** Rendered on the right side of the mobile header (optional). */
  mobileHeaderRight?: ReactNode;

  /** Extra classes for the outer fixed overlay */
  overlayClassName?: string;

  /** Extra classes for the inner modal/drawer container */
  containerClassName?: string;

  /** Extra classes for the scroll area (the single scroll owner) */
  scrollClassName?: string;

  /** Extra classes for the content wrapper inside the scroll area */
  contentClassName?: string;

  /** By default, desktop shows a click-to-close backdrop. */
  showDesktopBackdrop?: boolean;
  onDesktopBackdropClick?: () => void;

  /** Optional motion props for the outer overlay (used by callers that already use AnimatePresence). */
  overlayMotionProps?: MotionProps;

  /** When false, do not render back/close controls and do not close on backdrop click. */
  canClose?: boolean;

  /** Override z-index for special overlays (e.g. terms updates above admin overlays). */
  zIndex?: string | number;

  /** When true, use minimal gap below mobile header (smaller spacer). Use for detail modals where content should sit closer to header. */
  compactSpacer?: boolean;
};

/**
 * Standard full-screen shell for mobile (fixed header + spacer + single scroll area)
 * and centered modal for desktop (xl+).
 *
 * This intentionally centralizes the header geometry so create/edit/detail flows
 * behave consistently across ios-pwa, ios-safari, android-pwa, android-browser.
 */
export function FullscreenShell({
  title,
  onClose,
  onBack,
  children,
  mobileHeaderRight,
  overlayClassName,
  containerClassName,
  scrollClassName,
  contentClassName,
  showDesktopBackdrop = true,
  onDesktopBackdropClick,
  overlayMotionProps,
  canClose = true,
  zIndex = 'var(--z-modal)',
  compactSpacer = false,
}: FullscreenShellProps) {
  const back = onBack ?? onClose;

  const mobileHeaderPadTop = 'calc(var(--tf-safe-top) + var(--tf-mobile-header-pad-top))';
  const mobileSpacerH =
    'calc(var(--tf-safe-top) + var(--tf-mobile-header-pad-top) + var(--tf-mobile-header-h) + var(--tf-mobile-header-gap) + var(--tf-mobile-header-extra-gap))';

  return (
    <motion.div
      className={cn(
        'fixed inset-0 bg-white xl:bg-transparent xl:fixed xl:inset-0 xl:talentfunn-modal-overlay flex items-start xl:items-center justify-center p-0 xl:p-4 xl:pt-4 xl:pb-4',
        overlayClassName
      )}
      style={{ zIndex }}
      {...overlayMotionProps}
    >
      {/* Desktop: separate backdrop so modal content can sit above it with higher z-index */}
      {showDesktopBackdrop && canClose && (
        <div
          className="hidden xl:block fixed inset-0 talentfunn-modal-overlay backdrop-blur-md"
          style={{ zIndex: 'var(--z-overlay)' }}
          onClick={onDesktopBackdropClick ?? onClose}
          aria-hidden="true"
        />
      )}

      <div
        className={cn(
          // Mobile: use css-driven viewport height (svh/dvh) to avoid 100vh "too tall" issues under browser UI.
          // Desktop (xl+): keep auto height + max height cap.
          'bg-white w-full tf-fullscreen-shell xl:min-h-0 xl:h-auto xl:max-h-[85vh] rounded-none xl:rounded-3xl max-w-none xl:max-w-4xl xl:w-full pb-4 xl:pb-0 flex flex-col shadow-none xl:shadow-2xl my-0 xl:my-auto overflow-hidden xl:relative xl:z-[51]',
          compactSpacer && 'fullscreen-shell-compact-spacer',
          containerClassName
        )}
      >
        {/* Handle bar - desktop modal only */}
        <div className="hidden xl:flex justify-center pt-3 pb-2 flex-shrink-0">
          <div className="w-12 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Mobile fixed header */}
        <div
          className="xl:hidden fixed left-0 right-0 top-0 z-20 flex-shrink-0 min-h-[3.5rem] bg-white border-b border-gray-200 px-4 pb-3 flex items-center justify-between rounded-none"
          style={{ paddingTop: mobileHeaderPadTop }}
        >
          <div className="flex items-center gap-2 min-w-0">
            {canClose ? (
              <button
                onClick={back}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Tilbake"
              >
                <ArrowLeft className="w-5 h-5 text-gray-700" />
              </button>
            ) : (
              <div aria-hidden="true" className="w-10 h-10" />
            )}
            <h2 className="text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pt-[4px] pb-[4px] truncate">
              {title}
            </h2>
          </div>

          {mobileHeaderRight ? (
            mobileHeaderRight
          ) : (
            <div aria-hidden="true" className="w-10 h-10" />
          )}
        </div>

        {/* Spacer so scroll area starts below mobile header */}
        <div className="xl:hidden flex-shrink-0" style={{ height: mobileSpacerH }} aria-hidden="true" />

        {/* Desktop header */}
        <div className="hidden xl:flex sub-page-header flex-shrink-0 bg-white border-b border-gray-200 px-6 pt-4 pb-4 items-center justify-between rounded-t-3xl">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pt-[4px] pb-[4px] truncate">
            {title}
          </h2>
          {canClose ? (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Lukk"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          ) : (
            <div aria-hidden="true" className="w-10 h-10" />
          )}
        </div>

        {/* Single scroll owner */}
        <div className={cn('flex-1 min-h-0 flex flex-col overflow-y-auto ios-scroll', scrollClassName)}>
          <div className={cn('px-6 py-6 pb-12 xl:pb-16 flex-1 min-h-0', contentClassName)}>{children}</div>
        </div>
      </div>
    </motion.div>
  );
}

